import React, { useEffect, useState } from 'react'
import {
  AddRounded,
  Delete,
  LocalOffer,
  LocalOfferOutlined,
  MailRounded,
  PersonAdd,
  PersonRemove,
  Sms,
} from '@mui/icons-material'
import { CircularProgress } from '@mui/material'
import { useStores } from '@trivie/core'
import { AddTags } from '@trivie/ui-web/components/add-tags'
import { DataTable, TableHelper } from '@trivie/ui-web/components/data-table/data-table'
import { MessageDialog } from '@trivie/ui-web/components/message-dialog'
import { observer } from 'mobx-react-lite'
import { Toolbar } from '@trivie/ui-web/components/table-toolbar'
import { AddUsersToGroup } from '../components/add-users-to-group/add-users-to-group'
import { color } from '@trivie/ui-web/theme'
import { AddUsersToTag } from '../screens/people/tags/add-users/add-users-dialog'
import { useAlert, useConfirm } from '@trivie/ui-web/components/dialogs/confirmation-dialog'
import { uniq } from 'lodash'
import { Dialog } from '@trivie/ui-web/components/dialogs/dialog'
import { AutocompleteSelector } from '../components/autocomplete-selector'

interface UserTableProps {
  tableHelper: TableHelper
  forceLoading?: boolean
  openFilter?: boolean
  restricted?: boolean
  columns?: any
  exportFilename?: string
}

export const UserTable = observer((props: UserTableProps) => {
  const { exportFilename, tableHelper, columns, restricted, openFilter, forceLoading } = props

  const confirm = useConfirm()
  const alert = useAlert()

  const { manageUserStore, assignmentStore, tagStore, groupStore } = useStores()
  const {
    permissions,
    resendInvitation,
    deleteUsers,
    getTagCategories,
    tagCategories,
    enterpriseTier,
    freeTier,
    gdpr,
    changeStatus,
  } = manageUserStore
  const { removeUsersFromTags } = tagStore
  const { getAddedGroupUsers, getExcludedGroupUsers } = groupStore

  const [selectedRows, setSelectedRows] = useState<any>([])
  const [loading, setLoading] = useState<boolean>(tableHelper.custom ? false : true)

  // Dialogs
  const [showSendMessage, setShowSendMessage] = useState(false) // message
  const [showAddTags, setShowAddTags] = useState(false) // add tags to user
  const [showAddUsers, setShowAddUsers] = useState(false) // add user to group
  const [showAddUserToTag, setShowAddUserToTag] = useState(false) // add user to tag
  const [showUserActivationDialog, setShowUserActivationDialog] = useState(false) // show user activation/deactivation dialog

  /* Doesn't require a selection - can appear in toolbar or in more menu if > 1 */
  const getAdditionalActions = () => {
    switch (tableHelper.parent) {
      case 'assignments':
        return []
      case 'groups':
        return [
          {
            label: 'Add Users',
            icon: (
              <AddRounded
                htmlColor={
                  !permissions?.permissions.some((p) => p.code === 'can_edit_user')
                    ? color.shade20
                    : color.shade70
                }
              />
            ),
            onClick: () => setShowAddUsers(true),
            disabled: !permissions?.permissions.some((p) => p.code === 'can_edit_user'),
          },
        ]
      case 'tags':
        return [
          {
            label: 'Add Users',
            icon: (
              <LocalOffer
                htmlColor={
                  !permissions?.permissions.some((p) => p.code === 'can_edit_user')
                    ? color.shade20
                    : color.shade70
                }
              />
            ),
            onClick: () => setShowAddUserToTag(true),
            disabled: !permissions?.permissions.some((p) => p.code === 'can_edit_user'),
          },
        ]
      default:
        return []
    }
  }

  const defaultActions = freeTier
    ? []
    : [
        {
          label: 'Message',
          icon: Sms,
          onClick: () => setShowSendMessage(true),
          disabled:
            (tableHelper.rows
              .filter((row) => selectedRows.includes(row.id))
              .map((row: any) => row.user_status)
              .some((status) => ['Deactivated', 'Pending'].includes(status)) &&
              !tableHelper.rows
                .filter((row) => selectedRows.includes(row.id))
                .map((row: any) => row.user_status)
                .some((status) => ['Active'].includes(status))) ||
            !permissions?.permissions.some((p) => p.code === 'can_message_user'),
        },
      ]

  const getStatusList = () => {
    let activeSelection = false
    const statusList = uniq(
      tableHelper.rows
        .filter((row) => selectedRows.includes(row.id))
        .map((row: any) => row.user_status),
    )

    if (statusList.some((status) => ['Active', 'Pending'].includes(status))) {
      // If a selected user is either active or pending show deactivate
      activeSelection = true
    } else {
      // Otherwise show activate
      activeSelection = false
    }
    return activeSelection
  }

  /* Get toolbar actions */
  const getActions = () => {
    const activeSelection = getStatusList()
    switch (tableHelper.parent) {
      case 'assignments':
        return [
          ...defaultActions,
          {
            label: 'Exclude Users',
            icon: AddRounded,
            onClick: () => confirmExclude(false),
            disabled: !permissions?.permissions.some((p) => p.code === 'can_edit_user'),
          },
        ]
      case 'paths':
        return [...defaultActions]
      case 'topics':
        return [...defaultActions]
      case 'quizzes':
        return [...defaultActions]
      case 'groups':
        return [
          ...defaultActions,
          {
            label: 'Exclude Users',
            icon: AddRounded,
            onClick: () => confirmExclude(true),
            disabled: !permissions?.permissions.some((p) => p.code === 'can_edit_user'),
          },
        ]
      case 'tags':
        return [
          ...defaultActions,
          {
            label: 'Untag Users',
            icon: LocalOfferOutlined,
            onClick: () =>
              removeUsersFromTags(selectedRows, [tableHelper.id]).then(() => {
                load()
                setSelectedRows([])
              }),
            disabled: !permissions?.permissions.some((p) => p.code === 'can_edit_user'),
          },
        ]
      default:
        return !enterpriseTier
          ? [...defaultActions]
          : [
              ...defaultActions,
              {
                label: 'Resend Invite',
                icon: MailRounded,
                onClick: confirmResend,
                disabled: !permissions?.permissions.some((p) => p.code === 'can_create_user'),
              },
              {
                label: 'Add Tag',
                icon: LocalOffer,
                onClick: () => setShowAddTags(true),
                disabled: !permissions?.permissions.some((p) => p.code === 'can_edit_user'),
              },
              {
                label: activeSelection ? 'Deactivate' : 'Reactivate',
                icon: activeSelection ? PersonRemove : PersonAdd,
                onClick: () => setShowUserActivationDialog(true),
                disabled:
                  selectedRows?.length === 0 ||
                  !permissions?.permissions.some((p) => p.code === 'can_edit_user'),
              },
              {
                label: 'Delete',
                icon: Delete,
                onClick: confirmDelete,
                disabled: !permissions?.permissions.some((p) => p.code === 'can_delete_user'),
              },
            ]
    }
  }

  const confirmResend = () => {
    confirm({
      title: 'Resend Invitations',
      confirmationMessage: `${selectedRows.length} user${selectedRows.length === 1 ? '' : 's'} ${selectedRows.length === 1 ? 'has' : 'have'} been sent an invitation message.`,
      dialogText: `Are you sure you want to resend a welcome message to ${selectedRows.length} user${selectedRows.length === 1 ? '' : 's'}?`,
    })
      .then(() => {
        resendInvitation(selectedRows)
        setSelectedRows([])
      })
      .catch(() => {})
  }

  const confirmDelete = () => {
    alert({
      title: 'Delete Users',
      message: `Are you sure you want to delete ${selectedRows.length} user${
        selectedRows.length > 1 ? 's' : ''
      }?`,
      confirmationText: 'Yes',
      cancellationText: 'No',
      danger: true,
    }).then(() => {
      deleteUsers(
        selectedRows,
        () => {
          alert({
            title: 'Success',
            message: `${selectedRows.length} user${
              selectedRows.length > 1 ? 's have' : ' has'
            } been removed from the system.`,
            confirmationText: 'Ok',
            hideCancelButton: true,
          })
          setTimeout(() => {
            load()
            setSelectedRows([])
          }, 400)
        },
        (e) => {
          alert({
            title: 'Error Deleting Users',
            message: e.message,
            confirmationText: 'Ok',
            hideCancelButton: true,
            danger: true,
          })
        },
      )
    })
  }

  const confirmExclude = (group?: boolean) => {
    confirm({
      title: 'Exclude Users',
      dialogText: `Are you sure you want to exclude ${selectedRows.length} user${
        selectedRows.length > 1 ? 's' : ''
      }?`,
      confirmationMessage: `${selectedRows.length} user${
        selectedRows.length > 1 ? 's have' : ' has'
      } been excluded.`,
    })
      .then(() => {
        if (group) {
          groupStore.excludeUsers({ user_ids: selectedRows }).then(() => {
            load()
            if (tableHelper.id) {
              assignmentStore.getAddedUsers(tableHelper.id)
              assignmentStore.getExcludedUsers(tableHelper.id)
            }
            setSelectedRows([])
          })
        } else {
          if (tableHelper.id) {
            assignmentStore.excludeUsers(tableHelper.id, { user_ids: selectedRows }).then(() => {
              load()
              assignmentStore.getAddedUsers(tableHelper.id!)
              setSelectedRows([])
            })
          }
        }
      })
      .catch(() => {})
  }

  const [tableActions, setTableActions] = useState<any>(getActions())
  const [additionalActions] = useState<any>(getAdditionalActions())

  const [selectedOptions, setSelectedOptions] = useState<any>([])

  useEffect(() => {
    if (permissions) {
      setTableActions(getActions())
    }
  }, [permissions?.permissions])

  useEffect(() => {
    setLoading(Boolean(forceLoading))
  }, [forceLoading])

  useEffect(() => {
    load()
    if (enterpriseTier) {
      getTagCategories()
    }
  }, [])

  const [isActivating, setIsActivating] = useState(false)
  useEffect(() => {
    setTableActions(getActions())
    const active = getStatusList()
    setIsActivating(active)
    setSelectedOptions(
      tableHelper.rows
        .filter((row) => selectedRows.includes(row.id))
        .map((user) => {
          const { first_name, last_name, photo_url, email } = user
          return {
            type: active ? 'Deactivated Users' : 'Active Users',
            label: `${first_name} ${last_name}`,
            subLabel: email,
            photo_url: photo_url,
            id: user.id,
          }
        }),
    )
  }, [selectedRows])

  const load = () => {
    if (!tableHelper.custom) {
      setLoading(true)
      tableHelper.load().then(() => setLoading(false))
    }
  }

  const save = (cols: any, filter: any) => {
    setLoading(true)
    tableHelper.save(cols, filter).then(() => setLoading(false))
  }

  const handleAddItem = (selected: any[], removing?) => {
    setSelectedOptions(selected)
  }

  const getAutocompleteOptions = () => {
    const opts = [] as any
    tableHelper.rows
      .filter((o) => {
        if (isActivating) {
          return o.user_status === 'Active'
        } else {
          return o.user_status === 'Deactivated'
        }
      })
      .map((user) => {
        const { first_name, last_name, photo_url, email } = user
        if (first_name && last_name) {
          opts.push({
            type: 'Users',
            label: `${first_name} ${last_name}`,
            subLabel: email,
            photo_url: photo_url,
            id: user.id,
          })
        } else {
          opts.push({
            type: 'Users',
            label: email,
            photo_url: photo_url,
            id: user.id,
          })
        }
      })
    return opts
  }

  return loading ? (
    <div style={LOADER}>
      <CircularProgress />
    </div>
  ) : (
    <>
      <DataTable
        gdpr={gdpr}
        filterModel={tableHelper.filterModel}
        sortModel={tableHelper.sortModel}
        columnVisibilityModel={tableHelper.columnVisibilityModel}
        rows={
          // filter rows based on if activating or deactivating
          tableHelper.rows
        }
        columns={tableHelper.columns}
        restricted={restricted}
        allowNewReport
        tableHelper={tableHelper}
        searchPlaceholder="Search People"
        onSaveLayout={tableHelper?.saveLayout}
        onSaveFilters={save}
        toolbar={
          <Toolbar
            additionalActions={additionalActions}
            selected={selectedRows}
            actions={tableActions}
          />
        }
        exportFilename={
          exportFilename
            ? exportFilename
            : tableHelper.parent !== 'users'
              ? `${tableHelper.parent}_users`
              : 'users'
        }
        tagCategories={tagCategories}
        onRowSelectionModelChange={(param) => setSelectedRows(param)}
        rowSelectionModel={selectedRows}
        openFilter={openFilter}
      />

      {showUserActivationDialog && (
        <Dialog
          maxWidth="md"
          onCancel={() => setShowUserActivationDialog(false)}
          onConfirm={() => {
            setShowUserActivationDialog(false)
            confirm({
              danger: true,
              title: isActivating ? 'Deactivate Users' : 'Reactivate Users',
              confirmationMessage: `${selectedRows.length} user${selectedRows.length === 1 ? '' : 's'} ${selectedRows.length === 1 ? 'has' : 'have'} been ${isActivating ? 'deactivated' : 'reactivated'}.`,
              dialogText: `Are you sure you want to ${isActivating ? 'deactivate' : 'reactivate'} ${selectedRows.length} user${selectedRows.length === 1 ? '' : 's'}?`,
            })
              .then(() => {
                changeStatus(
                  isActivating ? 'deactivate' : 'activate',
                  selectedOptions.map((user) => user.id),
                ).then(() => {
                  setSelectedRows([])
                  setTimeout(() => {
                    load()
                  }, 400)
                })
              })
              .catch(() => {
                setShowUserActivationDialog(true)
              })
          }}
          onClose={() => {
            setShowUserActivationDialog(false)
          }}
          title={isActivating ? 'Deactivate Users' : 'Reactivate Users'}
          confirmBtnLabel={isActivating ? 'Deactivate' : 'Reactivate'}
          open={showUserActivationDialog}
        >
          <div
            style={{
              height: '50vh',
              overflowY: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <AutocompleteSelector
              variant="users"
              open
              showAvatar
              options={getAutocompleteOptions()}
              onSelectionChange={handleAddItem}
              preselectIds={selectedRows.map((id) => ({ id, type: 'Users' }))}
            />
          </div>
        </Dialog>
      )}
      {showSendMessage && (
        <MessageDialog
          ids={selectedRows}
          open={showSendMessage}
          userCount={(selectedRows ?? []).length}
          messageType="generic"
          onClose={() => {
            setSelectedRows([])
            setShowSendMessage(false)
          }}
        />
      )}
      {showAddTags && (
        <AddTags
          table
          open={showAddTags}
          users={selectedRows}
          onClose={() => {
            setShowAddTags(false)
            setSelectedRows([])
          }}
          onConfirm={() => {
            setShowAddTags(false)
            setSelectedRows([])
            load()
          }}
        />
      )}
      {showAddUsers && (
        <AddUsersToGroup
          open={showAddUsers}
          onClose={() => {
            getExcludedGroupUsers()
            getAddedGroupUsers()
            setShowAddUsers(false)
            load()
          }}
        />
      )}
      {showAddUserToTag && (
        <AddUsersToTag
          refresh={load}
          open={showAddUserToTag}
          tagId={tableHelper.id}
          onClose={() => setShowAddUserToTag(false)}
        />
      )}
    </>
  )
})

const LOADER: React.CSSProperties = {
  display: 'flex',
  flex: 1,
  justifyContent: 'center',
  flexDirection: 'column',
  alignItems: 'center',
  height: window.innerHeight / 1.5,
}
